import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isFailure, isSuccess } from '@carvana/merch-result';
import { useContext, useEffect } from 'react';
import { httpWithAuth } from '~/clients';
import { GET_DELIVERY_PICKUP } from '~/constants/endpoints';
import { useUserLocation } from '~/context/common/UserContext';
import AvailabilityContext, {
  AvailabilityPayloads,
  PageAvailabilityData
} from '~/context/home/AvailabilityContext';
import { DeliveryPickupResponse } from '~/services/common/contracts';
import { BrowserCookies } from '~/utilities';
import generateErrorLogger from '~/utilities/errorHandlers';

const deliveryPickupNinetyNinthPercentile = 2200;

function chunkArray(array: AvailabilityPayloads[], chunkSize: number) {
  const chunks: AvailabilityPayloads[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }

  return chunks;
}

export default function useGetBulkAvailability() {
  const {
    availabilityPayloads: {
      setData: setPayloads,
      data: payloads,
      isLoaded: isPayloadsLoaded
    },
    carouselVehicleAvailability: {
      data: carouselPayloads,
      isLoaded: isCarouselLoaded
    },
    pageAvailability: { setData, setError, setLoading },
    recentVehicleAvailability: {
      data: recentPayloads,
      isLoaded: isRecentLoaded
    },
    suggestedVehicleAvailability: {
      data: suggestedPayloads,
      isLoaded: isSuggestedLoaded
    }
  } = useContext(AvailabilityContext);
  const { zip } = useUserLocation();

  useEffect(() => {
    if (
      !(isSuggestedLoaded && isRecentLoaded && isCarouselLoaded) ||
      isPayloadsLoaded
    ) {
      return;
    }

    const rawPayloads = [
      ...recentPayloads,
      ...suggestedPayloads,
      ...carouselPayloads
    ];

    const uniquePayloads = rawPayloads.filter((payload, index, self) => {
      // Check if the current index is the first occurrence of the vehicleId
      return self.findIndex(p => p.vehicleId === payload.vehicleId) === index;
    });

    setPayloads(uniquePayloads);
  }, [
    carouselPayloads,
    isCarouselLoaded,
    isPayloadsLoaded,
    isRecentLoaded,
    isSuggestedLoaded,
    payloads,
    recentPayloads,
    setPayloads,
    suggestedPayloads
  ]);

  useEffectWithController(
    controller => {
      if (payloads.length === 0 || !isPayloadsLoaded) return;

      setLoading();

      // Split payloads into chunks of 50(api limit)
      const payloadChunks = chunkArray(payloads, 50);
      const fetchPromises = payloadChunks.map(chunk =>
        httpWithAuth
          .post<DeliveryPickupResponse>(
            GET_DELIVERY_PICKUP,
            {
              vehicles: chunk,
              zipCode: zip
            },
            {
              headers: {
                browsercookieid: BrowserCookies.get('BrowserCookieId') ?? '',
                clientid: 'home_ui', //TODO update to Katie Client Id on rebase depending whos goes in first also call fails if not vdp ui(need to look into that)
                correlationId: ''
              },
              timeout: deliveryPickupNinetyNinthPercentile,
              signal: controller.signal
            }
          )
          .then(({ data }) => data)
      );

      Promise.all(fetchPromises)
        .then(results => {
          // Combine all results from the chunks and convert to a single object so that we only iterate once
          const combinedResults = results.reduce<PageAvailabilityData>(
            (acc, result) => {
              if (isSuccess(result)) {
                result.content.forEach(v => {
                  acc[v.vehicleId] = {
                    soonestPickup: v.soonestPickup,
                    soonestDelivery: v.soonestDelivery
                  };
                });
              } else if (isFailure(result)) {
                setError(new Error(result.errorMessage));
              }

              return acc;
            },
            {}
          );

          setData(combinedResults);
        })
        .catch(
          generateErrorLogger('getBulkAvailability', {}, { callback: setError })
        );
    },
    [isPayloadsLoaded, payloads, setData, setError, setLoading, zip]
  );
}
